import { useState } from 'react';
import { useIsDesktopSize } from '@/hooks/useIsDesktopSize.ts';
import { UpdateSendingInterval } from '@/routes/device/update-sending-interval/types.ts';
import {
	UpdateDeviceSendingIntervalSchema,
	updateDeviceSendingIntervalSchema,
	UpdateIntervalLabels
} from '@/routes/device/update-sending-interval/sending-interval-schema.ts';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetFooter,
	SheetHeader,
	SheetTitle,
	SheetTrigger
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button.tsx';
import { cn } from '@/lib/utils.ts';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { toast } from 'sonner';
import { getUIErrorMessage } from '@/utils/errors';
import { useMutation } from '@tanstack/react-query';
import { updateSendingInterval } from '@/routes/device/constants.ts';
import Spinner from '@/components/spinner/Spinner.tsx';
import { ClockArrowUp } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select.tsx';
import { useParams } from 'react-router';

export type UpdateSendingIntervalFormProps = {
	isOpen: boolean;
	handleToggleForm: () => void;
	currentInterval: number;
};

export const UpdateSendingIntervalForm = (props: UpdateSendingIntervalFormProps) => {
	const { isOpen, handleToggleForm, currentInterval } = props;
	const { deviceId } = useParams();
	const [showConfirmation, setShowConfirmation] = useState(false);
	const [error, setError] = useState('');

	const { isDesktop } = useIsDesktopSize();

	const updateSendingIntervalForm = useForm<UpdateDeviceSendingIntervalSchema>({
		resolver: zodResolver(updateDeviceSendingIntervalSchema)
	});

	const { isPending: isSaving, mutate: updateDeviceSendingInterval } = useMutation({
		mutationFn: (data: UpdateSendingInterval) => updateSendingInterval(data, deviceId!),
		mutationKey: ['sending-interval'],
		onSuccess: () => {
			updateSendingIntervalForm.reset();
			handleToggleForm();
			setShowConfirmation(false);
			toast('Sending interval updated successfully');
		},
		onError: error => {
			setError(getUIErrorMessage(error));
		}
	});

	const parseNumber = (s: string) => parseInt(s, 10);

	const handleDeviceUpdateSendingInterval = () => {
		const submitForm = updateSendingIntervalForm.handleSubmit(values => {
			const payload: UpdateSendingInterval = {
				updateManifestId: parseNumber(values.updateManifestId)
			};
			updateDeviceSendingInterval(payload);
		});
		submitForm();
	};

	const handleCloseForm = () => {
		handleToggleForm();
		setShowConfirmation(false);
		setError('');
	};

	return (
		<Sheet open={isOpen} onOpenChange={handleCloseForm}>
			<SheetTrigger>
				<Button asChild variant="outline" onClick={handleToggleForm} className="flex justify-start w-fit">
					<span>
						<ClockArrowUp />
						Update sending interval
					</span>
				</Button>
			</SheetTrigger>
			<SheetContent
				side={!isDesktop ? 'bottom' : 'right'}
				className={cn('h-full p-5 overflow-auto', { 'sm:max-w-xl ': isDesktop, 'w-full h-5/6': !isDesktop })}
			>
				<SheetHeader>
					<SheetTitle>Update device sending interval</SheetTitle>
					<SheetDescription>Fill in the details. Click update when you're done.</SheetDescription>
				</SheetHeader>
				<Form {...updateSendingIntervalForm}>
					<FormItem>
						<FormLabel>Current sending interval</FormLabel>
						<FormControl>
							<div className="border border-input rounded-md px-3 py-2 text-sm text-muted-foreground">
								{UpdateIntervalLabels[currentInterval] ?? currentInterval}
							</div>
						</FormControl>
					</FormItem>
					<FormField
						control={updateSendingIntervalForm.control}
						name="updateManifestId"
						render={({ field }) => (
							<FormItem>
								<FormLabel>New sending interval</FormLabel>

								<Select
									onValueChange={val => {
										console.log(val);
										field.onChange(val);
									}}
									value={field.value ?? ''}
								>
									<FormControl>
										<SelectTrigger className="w-full">
											<SelectValue placeholder="Select a new sending interval" />
										</SelectTrigger>
									</FormControl>
									<SelectContent>
										{Object.entries(UpdateIntervalLabels).map(([value, label]) => (
											<SelectItem key={value} value={value}>
												{label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<FormMessage />
							</FormItem>
						)}
					/>
				</Form>

				{error && (
					<div className="text-red-500 text-sm text-center">
						<p>{error}</p>
					</div>
				)}

				<SheetFooter>
					{!showConfirmation ? (
						<Button type="submit" onClick={() => setShowConfirmation(true)}>
							Update interval
						</Button>
					) : (
						<div className="flex flex-col gap-2 text-sm justify-center items-center">
							<p>Are you sure you want to update the device's sending interval?</p>
							<div className="flex gap-2 w-full justify-center">
								<Button
									className="flex-1"
									variant="secondary"
									onClick={() => {
										setShowConfirmation(false);
										setError('');
									}}
									disabled={isSaving}
								>
									Cancel
								</Button>
								<Button
									className="flex-1"
									type="submit"
									onClick={handleDeviceUpdateSendingInterval}
									disabled={isSaving}
								>
									{error ? 'Try again ' : 'Update'}
									{isSaving && <Spinner className="ml-2" />}
								</Button>
							</div>
						</div>
					)}
				</SheetFooter>
			</SheetContent>
		</Sheet>
	);
};
